import { ChangeDetectorRef, Component } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { NgSelectSearch } from '@flyfreely-portal-ui/ng-select';
import { FieldType } from '@ngx-formly/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormlyGraphQlPaginationService } from './helpers';

/**
 * A version of the Formly ng-select that can load items dynamically and paginate using a scoped graph-ql service that is passed in.
 * The scoped service should be passed in using the 'service' value in the Formly template options.
 * For an example implementation, see the jurisdictions edit dialogue in the admin app, or `docs/libraries.md`.
 *
 * Setting `validateSelection: true` in the field props marks the control invalid
 * (`Invalid Selection`) when the current value is not in the loaded items. Only
 * enable it for fields whose service resolves and re-injects the original value
 * on every emit (see FormlyPilotHelperService), otherwise a value outside the
 * fetched page is misreported as invalid.
 */
@Component({
    selector: 'formly-graphql-ng-select',
    template: `
        <div class="form-group">
            <ng-select
                [items]="items"
                [clearable]="!to.required"
                [formControl]="formControl"
                [bindLabel]="to.labelProp || 'label'"
                [bindValue]="to.valueProp || 'value'"
                appendTo="body"
                [loading]="loading"
                [virtualScroll]="true"
                [searchFn]="searchFn"
                (scrollToEnd)="onSelectScroll()"
                (search)="onSelectSearch($event)"
                (close)="onSelectClose()"
                (open)="onSelectOpen()"
            >
            </ng-select>
        </div>
    `
})
export class FormlyGraphQlNgSelect extends FieldType {
    graphQlService: FormlyGraphQlPaginationService;
    items: any = [];
    loading = false;
    isOpen = false;
    searchFn: (term: string, item: any) => boolean;

    private serviceLoading = false;
    private receivedResults = false;

    private selectionValidator = (
        control: AbstractControl
    ): ValidationErrors | null =>
        this.invalidSelection
            ? { invalidSelection: { message: 'Invalid Selection' } }
            : null;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(private changeDetector: ChangeDetectorRef) {
        super();
    }

    get invalidSelection(): boolean {
        const value = this.formControl.value;
        return (
            this.receivedResults &&
            !this.serviceLoading &&
            value != null &&
            !this.items.some(
                (i: any) => i[this.to.valueProp || 'value'] === value
            )
        );
    }

    ngOnInit() {
        this.graphQlService = this.to.service;
        this.graphQlService.loading$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(loading => {
                this.loading = loading && this.isOpen;
                this.serviceLoading = loading;
                this.revalidateSelection();
                this.changeDetector.markForCheck();
            });
        this.graphQlService.results$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(results => {
                this.items = results;
                this.receivedResults = true;
                this.revalidateSelection();
                this.changeDetector.markForCheck();
            });
        if (this.to.validateSelection === true) {
            this.formControl.addValidators(this.selectionValidator);
        }
        this.graphQlService.refreshItems(this.formControl.value);
        this.searchFn = this.graphQlService.searchFn;
    }

    ngOnDestroy() {
        if (this.to.validateSelection === true) {
            this.formControl.removeValidators(this.selectionValidator);
            this.formControl.updateValueAndValidity({ emitEvent: false });
        }
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private revalidateSelection() {
        if (this.to.validateSelection !== true) {
            return;
        }
        this.formControl.updateValueAndValidity();
        if (this.formControl.hasError('invalidSelection')) {
            this.formControl.markAsTouched();
        }
    }

    onSelectScroll() {
        this.graphQlService.onSelectScroll();
    }

    onSelectSearch(search: NgSelectSearch) {
        this.graphQlService.onSelectSearch(search);
    }

    onSelectOpen() {
        this.isOpen = true;
        if (this.graphQlService.onSelectOpen) {
            this.graphQlService.onSelectOpen();
        }
    }

    onSelectClose() {
        this.isOpen = false;
        this.graphQlService.onSelectClose();
    }
}
